import { Button, Group, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import FormCard from "src/components/FormCard";
import {
  SeriesInterval,
  useGetMeetingSeriesDetailsLazyQuery,
  useUpdateMeetingSeriesMutation,
  useCreateMeetingSeriesMutation,
  CreateMeetingSeriesInput,
} from "src/generated/graphql";
import { array, boolean, date, number, object, string } from "yup";
import MeetingSeriesForm from "./MeetingSeriesForm";
import { useEffect } from "react";
import { addDaysToDate } from "src/utils/dateHelper";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";
import useWritePermission from "src/hooks/usePermission";

interface IMeetingDetailProps {
  seriesId?: string;
  isEdit: boolean;
  locationId: string;
  closeModal: () => void;
  onRefetch: () => void;
  isAllowWrite: boolean;
}

export type IFormProps = Omit<
  CreateMeetingSeriesInput,
  | "room"
  | "service"
  | "instructor"
  | "location"
  | "duration"
  | "session"
  | "privateSession"
  | "attendees"
> & {
  service?: string;
  instructor?: string;
  room?: string;
  duration: number;
  location: string;
  days?: number;

  session?: string;
  privateSession?: string;

  isRoomRequired: boolean;
  isEmployeeRequired: boolean;
  attendees?: string[];
};

const MeetingSeriesFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should must be least 2 letters")
    .max(50, "Name must not be greater than 50 letters")
    .required("Name is required"),
  description: string().max(200).notRequired(),
  service: string()
    .when("isPrivateSession", {
      is: false,
      then: () => string().required("Service is required"),
    })
    .nullable(),
  isEmployeeRequired: boolean(),
  isRoomRequired: boolean(),
  instructor: string().when("isEmployeeRequired", {
    is: true,
    then: () => string().required("Instructor is required"),
    otherwise: () => string(),
  }),
  room: string().when("isRoomRequired", {
    is: true,
    then: () => string().required("Room is required"),
    otherwise: () => string(),
  }),
  duration: number().required("Duration is required"),
  startAt: string()
    .required("Start Date is required")
    .test("dateNotInPast", "Date cannot be in the past", function (item) {
      let toTest = new Date(item);
      let startOfDay = new Date(new Date().setHours(0, 0, 0, 0));
      return toTest.valueOf() >= startOfDay.valueOf();
    }),
  maxOccupancy: number().required("Duration is required"),
  seriesInterval: string().required("Interval is required"),
  scheduleFrom: date().required("Schedule From is required"),
  scheduleTo: string()
    .when(["seriesInterval"], {
      is: (seriesInterval: SeriesInterval) =>
        seriesInterval !== SeriesInterval.Daily,
      then: () => date().required("Schedule To is required"),
    })
    .nullable(),
  days: string()
    .when("seriesInterval", {
      is: SeriesInterval.Daily,
      then: () => string().required("Days is required"),
    })
    .nullable(),
  dows: array().when("seriesInterval", {
    is: SeriesInterval.Weekly,
    then: () => array().min(1, "At least one box must be selected").required(),
  }),
  doms: array().when("seriesInterval", {
    is: SeriesInterval.Monthly,
    then: () => array().min(1, "At least one box must be selected").required(),
  }),

  session: string().when("isPrivateSession", {
    is: true,
    then: () => string().required("Session is required"),
    otherwise: () => string(),
  }),
  privateSession: string().when("session", {
    is: (session: string) => !!session,
    then: () => string().required("Duration is required"),
    otherwise: () => string(),
  }),
});

const ViewEditMeetingSeriesEvent = ({
  seriesId,
  isEdit,
  locationId,
  closeModal,
  onRefetch,
}: IMeetingDetailProps) => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.calendar);
  const theme = useMantineTheme();

  const [getMeetingSeriesDetails, { data, loading }] =
    useGetMeetingSeriesDetailsLazyQuery({
      fetchPolicy: "network-only",
    });

  const [createMeetingSeries, { loading: createSeriesLoading }] =
    useCreateMeetingSeriesMutation({
      onCompleted() {
        onRefetch();
        closeModal();
      },
    });

  const [updateMeetingSeries, { loading: updateSeriesLoading }] =
    useUpdateMeetingSeriesMutation({
      onCompleted() {
        onRefetch();
        closeModal();
      },
    });

  useEffect(() => {
    if (seriesId) {
      getMeetingSeriesDetails({
        variables: {
          id: seriesId,
        },
      });
    }
  }, [seriesId]);

  const form = useForm<IFormProps>({
    initialValues: {
      description: "",
      duration: Number(),
      isSelfEnroll: false,
      isWaitlistEnabled: false,
      name: "",
      waitlistMax: Number(),
      maxOccupancy: 0,
      room: "",
      instructor: "",
      imgUrl: "",
      service: "",
      startAt: "",
      location: "",
      seriesInterval: null,
      dows: [],
      doms: [],
      scheduleFrom: undefined,
      scheduleTo: undefined,

      isPrivateSession: false,

      session: "",
      privateSession: "",
      isRoomRequired: true,
      isEmployeeRequired: true,
      attendees: [],
    },
    validate: yupResolver(MeetingSeriesFormSchema),
  });

  const handleSubmit = async (values: Required<IFormProps>) => {
    const {
      name,
      description,
      maxOccupancy,
      isWaitlistEnabled,
      waitlistMax,
      isSelfEnroll,
      imgUrl,
      room,
      instructor,
      service,
      duration,
      startAt,
      scheduleFrom,
      scheduleTo,
      doms,
      dows,
      seriesInterval,
      days,

      session,
      privateSession,
      attendees,
      isPrivateSession,
    } = values;
    let totalMinutes = values.startAt.getMinutes() + values.duration;
    const _scheduledTo = days
      ? addDaysToDate(
          new Date(
            new Date(scheduleFrom).setHours(
              values.startAt.getHours() + Math.floor(totalMinutes / 60),
              totalMinutes % 60,
              0,
              0
            )
          ),
          days
        )
      : scheduleTo;
    if (isEdit) {
      const meetingDetailsObj = {
        name,
        description,
        maxOccupancy,
        isWaitlistEnabled,
        waitlistMax,
        isSelfEnroll,
        imgUrl,
        ...(instructor && { instructor: { id: instructor } }),
        ...(room && { room: { id: room } }),
        ...(service && { service: { id: service } }),
        ...(session && { session: { id: session } }),
        ...(privateSession && { privateSession: { id: privateSession } }),
        attendees: attendees.map((a) => ({ id: a })),
        isPrivateSession,
      };
      const schedulingDetailsObj = {
        duration,
        startAt,
        seriesInterval,
        doms,
        dows,
        scheduleFrom,
        scheduleTo: _scheduledTo,
      };
      updateMeetingSeries({
        variables: {
          id: seriesId!,
          data: {
            meetingDetails: meetingDetailsObj,
            schedulingDetails: schedulingDetailsObj,
          },
        },
      });
    } else {
      const seriesObj = {
        name,
        description,
        maxOccupancy,
        isWaitlistEnabled,
        waitlistMax,
        isSelfEnroll,
        imgUrl,
        location: { id: locationId },
        scheduleFrom,
        scheduleTo: _scheduledTo,
        duration,
        startAt,
        doms,
        dows,
        seriesInterval,
        ...(instructor && { instructor: { id: instructor } }),
        ...(room && { room: { id: room } }),
        ...(service && { service: { id: service } }),
        ...(session && { session: { id: session } }),
        ...(privateSession && { privateSession: { id: privateSession } }),
        attendees: attendees.map((a) => ({ id: a })),
        isPrivateSession,
      };

      createMeetingSeries({
        variables: {
          data: seriesObj,
        },
      });
    }
  };

  return (
    <FormCard
      title={"Basic Info"}
      loading={loading}
      onSave={form.onSubmit((values) =>
        handleSubmit(values as Required<IFormProps>)
      )}
      hideCard={true}
      mb={10}
      size="md"
      radius={4}
      isFormActive={false}
    >
      <MeetingSeriesForm
        form={form}
        isEdit={isEdit}
        meetingDetail={data?.meetingSeriesOne}
        disabled={!isAllowWrite}
      />

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={closeModal}
          disabled={createSeriesLoading || updateSeriesLoading}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          type="submit"
          disabled={createSeriesLoading || updateSeriesLoading || !isAllowWrite}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
          loading={createSeriesLoading || updateSeriesLoading}
        >
          Save
        </Button>
      </Group>
    </FormCard>
  );
};

export default ViewEditMeetingSeriesEvent;
